import PropTypes from "prop-types";
import React, { useRef, useState } from "react";

import { useResizeObserver } from "@swa-ui/browser";
import { classNames } from "@swa-ui/string";

import { Transition } from "../Transition";
import styles from "./FluidWidth.module.scss";

/**
 * FluidWidth provides a way to have a div accommodate additional content smoothly.
 *
 * Note:
 * We will need to learn better when this component should be used - for the most part, we should not
 * explicitly change an element's width, but instead allow the browser to flow naturally.
 */
export const FluidWidth = (props) => {
  const { children, className, id, onTransitionEnd } = props;
  const [contentWidth, setContentWidth] = useState(0);
  const ref = useRef(null);

  useResizeObserver({ callback: updateWidth, element: ref });

  return (
    <Transition {...getTransitionProps()}>
      <div {...getPropsContainer()}>{children}</div>
    </Transition>
  );

  function getTransitionProps() {
    return {
      animate: { width: `${contentWidth}px` },
      className: classNames(className, styles.fluidWidth),
      id,
      onTransformationEnd: handleTransformationEnd,
      ...(contentWidth && { initial: { width: `${contentWidth}px` } }),
    };
  }

  function getPropsContainer() {
    return {
      className: styles.container,
      ref,
    };
  }

  function handleTransformationEnd() {
    onTransitionEnd && onTransitionEnd();
  }

  function updateWidth(value) {
    setContentWidth(value[0].contentRect.width);
  }
};

FluidWidth.displayName = "FluidWidth";
FluidWidth.propTypes = {
  /** Content that will be rendered when Area is revealed. */
  children: PropTypes.node,

  /** Additional classes for positioning the component. */
  className: PropTypes.string,

  /**
   * id can be useful when another DOM element "controls" the concealed/revealed state. The
   * controlling element should use aria-control to reflect this correlation.
   */
  id: PropTypes.string,

  /**
   * Function called when the area is revealed or concealed. The revealed state will be returned as
   * a boolean.
   */
  onTransitionEnd: PropTypes.func,
};
